import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { timezoneService } from '../services/timezoneService';

const TicketCard = ({ ticket }) => {
  const [formattedDate, setFormattedDate] = useState('');

  useEffect(() => {
    const formatDate = async () => {
      // Use the same timezone conversion as other components
      const formatted = await timezoneService.formatDateTime(ticket.createdAt);
      setFormattedDate(formatted);
    };
    formatDate();
  }, [ticket.createdAt]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 1: return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 2: return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 3: return <ClockIcon className="h-4 w-4 text-orange-500" />;
      case 4: return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 5: return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'New';
      case 2: return 'In Progress';
      case 3: return 'Waiting Feedback';
      case 4: return 'Closed';
      case 5: return 'Reopened';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 3: return 'bg-yellow-100 text-yellow-800';
      case 4: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      case 4: return 'Critical';
      default: return 'Unknown';
    }
  };

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {getStatusIcon(ticket.status)}
            <span className="text-sm font-medium text-gray-900">
              {getStatusText(ticket.status)}
            </span>
            {ticket.isSlaBreached && (
              <XCircleIcon className="h-4 w-4 text-red-500" />
            )}
          </div>
          
          <Link 
            to={`/ticket/${ticket.id}`}
            className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors"
          >
            {ticket.title}
          </Link>
          
          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <UserIcon className="h-4 w-4 mr-1" />
              {ticket.userName}
            </div>
            {ticket.technicianName && (
              <div className="flex items-center">
                <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                {ticket.technicianName}
              </div>
            )}
            <div>{formattedDate}</div>
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
            {getPriorityText(ticket.priority)}
          </span>
          <span className="text-sm text-gray-500">
            {ticket.categoryName}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TicketCard; 